import { createLink, type LinkComponent } from '@tanstack/react-router'
import clsx from 'clsx'
import { type HTMLMotionProps, motion } from 'motion/react'
import {
  forwardRef,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode
} from 'react'
import {
  contentStyle,
  iconStyle,
  subTitleCompact,
  subTitleStyle,
  titleCompact,
  titleStyle,
  titleWithHover,
  titleWithoutSubtitle,
  wrapHeader,
  wrapHeaderWithIcon,
  wrapper,
  wrapperCompact,
  wrapperDisabled,
  wrapTitle,
  wrapTitleCompact,
  wrapWithHover
} from './Card.css'

export type CardProps = PropsWithChildren<{
  disabled?: boolean
  /** Tighter card with the subtitle stacked under the title (lists of persons, side panels). */
  compact?: boolean
  onClick?: (e: React.MouseEvent<HTMLElement>) => void
  href?: string
  icon?: ReactNode
  title?: ReactNode
  subtitle?: ReactNode
  metaData?: ReactNode[]
  classNames?: string[]
}>

const BasicLinkComponent = forwardRef<HTMLAnchorElement, HTMLMotionProps<'a'>>(
  (props, ref) => {
    return <motion.a ref={ref} {...props} />
  }
)

const MotionLinkComponent = createLink(BasicLinkComponent)

const MotionLink: LinkComponent<typeof BasicLinkComponent> = (props) => {
  return <MotionLinkComponent preload={'intent'} {...props} />
}

export const Card = ({
  disabled,
  compact = false,
  onClick,
  href,
  icon,
  title,
  subtitle,
  children,
  metaData,
  classNames = []
}: CardProps): ReactElement => {
  const isInteractive = !!onClick || !!href
  const className = clsx(
    ...classNames,
    wrapper,
    compact && wrapperCompact,
    isInteractive && wrapWithHover,
    disabled && wrapperDisabled
  )

  const commonProps = {
    initial: { x: 20, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: 20, opacity: 0 },
    className
  }

  const cardContent = (
    <>
      {icon && <div className={iconStyle}>{icon}</div>}
      <div className={clsx(wrapHeader, icon && wrapHeaderWithIcon)}>
        <div className={clsx(wrapTitle, compact && wrapTitleCompact)}>
          {title && (
            <span
              className={clsx(
                titleStyle,
                !subtitle && titleWithoutSubtitle,
                compact && titleCompact,
                isInteractive && titleWithHover
              )}
            >
              {title}
            </span>
          )}
          {subtitle && (
            <span className={clsx(subTitleStyle, compact && subTitleCompact)}>
              {subtitle}
            </span>
          )}
        </div>
        {children && <div className={contentStyle}>{children}</div>}
      </div>
      {metaData?.map((meta) => meta)}
    </>
  )

  if (href) {
    if (window.JobConfig) {
      return (
        <motion.a {...commonProps} href={href} onClick={onClick}>
          {cardContent}
        </motion.a>
      )
    }
    return (
      <MotionLink
        {...commonProps}
        className={className}
        to={href}
        onClick={onClick}
      >
        {cardContent}
      </MotionLink>
    )
  }

  return (
    <motion.div {...commonProps} onClick={onClick}>
      {cardContent}
    </motion.div>
  )
}
